import React, { useRef } from 'react';
import { motion, useInView } from 'motion/react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowRight, Target, Users, Lightbulb, Layers, Sparkles } from 'lucide-react';
import Layout from '../components/layout/Layout';
import SEOHead from '../components/SEOHead';

const TextReveal = ({ children, delay = 0 }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-60px" });
  return (
    <motion.div ref={ref} initial={{ opacity: 0, y: 40 }} animate={isInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] }}>
      {children}
    </motion.div>
  );
};

const About = () => {
  return (
    <Layout>
      <SEOHead
        title="About Buzz Craft Maven | Modern Creative Consultancy"
        description="Buzz Craft Maven is a modern creative consultancy designing brands, spaces, and experiences. Two verticals — digital marketing and interior design — under one unified vision."
        canonical="https://buzzcraftmaven.com/about"
        ogTitle="About Buzz Craft Maven | Modern Creative Consultancy"
        ogDescription="A premium creative consultancy combining digital growth with thoughtfully designed spaces."
        schemas={[
          { "@context": "https://schema.org", "@type": "AboutPage", "name": "About Buzz Craft Maven", "url": "https://buzzcraftmaven.com/about", "mainEntity": { "@type": "Organization", "name": "Buzz Craft Maven", "foundingDate": "2023", "areaServed": ["India", "Dubai", "UAE"] } },
          { "@context": "https://schema.org", "@type": "BreadcrumbList", "itemListElement": [{ "@type": "ListItem", "position": 1, "name": "Home", "item": "https://buzzcraftmaven.com" }, { "@type": "ListItem", "position": 2, "name": "About", "item": "https://buzzcraftmaven.com/about" }] }
        ]}
      />

      {/* Hero */}
      <section className="pt-32 pb-16 md:pt-40 md:pb-24 bg-sunrise-hero relative overflow-hidden" aria-label="About hero">
        <div className="absolute top-1/3 left-1/4 w-[400px] h-[400px] sun-orb sun-orb-primary opacity-30" />
        <div className="container mx-auto px-6 md:px-12 relative z-10">
          <nav className="text-sm text-[#8A8A8A] mb-8" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-[#FF6B35]">Home</Link> <span className="mx-2">/</span> <span className="text-[#2D2D2D]">About</span>
          </nav>
          <div className="max-w-4xl">
            <TextReveal>
              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-[#2D2D2D] leading-[1.05] mb-6">
                A Creative Consultancy, <span className="text-gradient-sunrise">Not an Agency.</span>
              </h1>
            </TextReveal>
            <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }}
              className="text-base md:text-lg text-[#5A5A5A] max-w-2xl leading-relaxed">
              Buzz Craft Maven exists at the intersection of digital strategy and spatial design. We build brands that are felt online and experienced offline.
            </motion.p>
          </div>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-20 md:py-28 bg-white" aria-label="Our story">
        <div className="container mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            <div>
              <TextReveal>
                <h2 className="font-display text-3xl md:text-4xl text-[#2D2D2D] mb-6">Two Verticals. <span className="text-gradient-sunrise">One Vision.</span></h2>
              </TextReveal>
              <TextReveal delay={0.1}>
                <p className="text-[#5A5A5A] text-base leading-relaxed mb-4">
                  Most agencies do one thing. We do two — intentionally. Buzz Craft Maven was built on a simple observation: the brands that win in 2026 are the ones that control their presence both digitally and physically.
                </p>
              </TextReveal>
              <TextReveal delay={0.15}>
                <p className="text-[#5A5A5A] text-base leading-relaxed mb-4">
                  Our <strong>Digital Solutions</strong> division handles brand strategy, performance marketing, social media, content production, founder branding, and website design. We build the engine that drives online growth.
                </p>
              </TextReveal>
              <TextReveal delay={0.2}>
                <p className="text-[#5A5A5A] text-base leading-relaxed">
                  Our <strong>Interior Design Solutions</strong> division creates residential, commercial, workspace, retail, and hospitality interiors. We design spaces that are extensions of brand identity — not just pretty rooms.
                </p>
              </TextReveal>
            </div>
            <div className="space-y-4">
              {[
                { num: '01', title: 'Strategy Before Tactics', desc: 'Every project starts with deep understanding. We diagnose before we prescribe.' },
                { num: '02', title: 'Brand-First Thinking', desc: 'Your brand identity drives every design choice, every campaign, every spatial decision.' },
                { num: '03', title: 'Outcome-Driven', desc: 'We measure success in business results — leads, conversions, dwell time, client satisfaction.' },
              ].map((item, i) => (
                <TextReveal key={item.num} delay={i * 0.1}>
                  <div className="flex items-start gap-4 p-5 rounded-xl bg-[#FFFBF5] border border-[#FF6B35]/10">
                    <span className="w-10 h-10 rounded-full bg-gradient-to-br from-[#FF6B35] to-[#FFB627] text-white flex items-center justify-center text-sm font-bold flex-shrink-0">{item.num}</span>
                    <div>
                      <h3 className="text-[#2D2D2D] font-semibold mb-1">{item.title}</h3>
                      <p className="text-[#5A5A5A] text-sm">{item.desc}</p>
                    </div>
                  </div>
                </TextReveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* What Sets Us Apart */}
      <section className="py-20 md:py-28 bg-[#FFFBF5]" aria-label="What sets us apart">
        <div className="container mx-auto px-6 md:px-12">
          <TextReveal>
            <div className="text-center max-w-2xl mx-auto mb-14">
              <h2 className="font-display text-3xl md:text-4xl text-[#2D2D2D]">What Sets <span className="text-gradient-sunrise">BCM Apart</span></h2>
            </div>
          </TextReveal>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { icon: Target, title: 'Strategic Clarity', desc: 'We don\u2019t start with deliverables. We start with business objectives and work backwards to the right channels, formats, and spaces.' },
              { icon: Layers, title: 'Cross-Vertical Thinking', desc: 'A restaurant needs great interiors AND great digital marketing. A startup needs a strong brand AND a fast website. We deliver both.' },
              { icon: Users, title: 'Partner Mentality', desc: 'We are invested in your success. We challenge assumptions, push for quality, and refuse to deliver mediocre work.' },
            ].map((v, i) => (
              <TextReveal key={v.title} delay={i * 0.1}>
                <div className="p-6 bg-white rounded-2xl border border-[#FF6B35]/8 hover:border-[#FF6B35]/20 transition-all h-full">
                  <div className="w-12 h-12 rounded-xl bg-[#FF6B35]/8 flex items-center justify-center mb-5">
                    <v.icon size={22} className="text-[#FF6B35]" />
                  </div>
                  <h3 className="font-display text-lg font-semibold text-[#2D2D2D] mb-2">{v.title}</h3>
                  <p className="text-[#5A5A5A] text-sm leading-relaxed">{v.desc}</p>
                </div>
              </TextReveal>
            ))}
          </div>
        </div>
      </section>

      {/* How We Work */}
      <section className="py-20 md:py-28 bg-white" aria-label="How we work">
        <div className="container mx-auto px-6 md:px-12">
          <TextReveal>
            <div className="text-center max-w-2xl mx-auto mb-14">
              <h2 className="font-display text-3xl md:text-4xl text-[#2D2D2D]">How We <span className="text-gradient-sunrise">Work</span></h2>
              <p className="text-[#5A5A5A] mt-4">A clear, structured process that ensures quality and accountability at every stage.</p>
            </div>
          </TextReveal>
          <div className="max-w-3xl mx-auto space-y-0">
            {[
              { n: '1', t: 'Discovery & Audit', d: 'We study your brand, market, competitors, and goals. For interiors, we visit the space. For digital, we audit your presence.' },
              { n: '2', t: 'Strategy & Roadmap', d: 'A custom plan with clear milestones, timelines, and budget. No surprises, no ambiguity.' },
              { n: '3', t: 'Design & Build', d: 'Our specialists execute — whether it\'s campaign creative, 3D renders, content production, or construction management.' },
              { n: '4', t: 'Launch & Optimize', d: 'We launch, measure, learn, and optimize. For digital, that means weekly reports. For interiors, final styling and handover.' },
            ].map((step, i) => (
              <TextReveal key={step.n} delay={i * 0.1}>
                <div className="relative flex items-start gap-6 pb-10 last:pb-0">
                  {i < 3 && <div className="absolute left-5 top-12 w-px h-full bg-gradient-to-b from-[#FF6B35] to-[#FFB627]/20" />}
                  <div className="relative z-10 w-10 h-10 rounded-full bg-gradient-to-br from-[#FF6B35] to-[#FFB627] flex items-center justify-center flex-shrink-0 shadow-lg shadow-orange-500/20">
                    <span className="text-white font-bold text-sm">{step.n}</span>
                  </div>
                  <div className="pt-1.5">
                    <h3 className="font-semibold text-[#2D2D2D] mb-1">{step.t}</h3>
                    <p className="text-[#5A5A5A] text-sm">{step.d}</p>
                  </div>
                </div>
              </TextReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Locations */}
      <section className="py-20 md:py-28 bg-[#FFFBF5]" aria-label="Our locations">
        <div className="container mx-auto px-6 md:px-12">
          <TextReveal><h2 className="font-display text-3xl md:text-4xl text-[#2D2D2D] mb-10 text-center">Where We <span className="text-gradient-sunrise">Operate</span></h2></TextReveal>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-3xl mx-auto">
            {[
              { city: 'Chennai', region: 'Headquarters', desc: 'Our home base. Full-service digital and interior design operations.' },
              { city: 'Hyderabad', region: 'India', desc: 'Growing presence serving the Telangana market and surrounding regions.' },
              { city: 'Dubai', region: 'UAE', desc: 'Middle East operations for international brands and hospitality projects.' },
            ].map((loc, i) => (
              <TextReveal key={loc.city} delay={i * 0.1}>
                <div className="p-6 bg-white rounded-2xl border border-[#FF6B35]/8 text-center">
                  <h3 className="font-display text-xl font-semibold text-[#2D2D2D] mb-1">{loc.city}</h3>
                  <p className="text-[#FF6B35] text-xs font-medium uppercase tracking-wide mb-3">{loc.region}</p>
                  <p className="text-[#5A5A5A] text-sm">{loc.desc}</p>
                </div>
              </TextReveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-br from-[#FF6B35] to-[#FFB627] text-center" aria-label="Call to action">
        <div className="container mx-auto px-6 md:px-12">
          <h2 className="font-display text-3xl md:text-4xl text-white mb-4">Ready to work with BCM?</h2>
          <p className="text-white/80 max-w-md mx-auto mb-8">Let's discuss your project and build something meaningful together.</p>
          <Link to="/contact">
            <motion.button className="px-10 py-4 bg-white text-[#FF6B35] rounded-full font-semibold shadow-xl hover:shadow-2xl transition-shadow inline-flex items-center gap-2" whileHover={{ scale: 1.02 }}>
              Start a Conversation <ArrowRight size={18} />
            </motion.button>
          </Link>
        </div>
      </section>
    </Layout>
  );
};

export default About;
